import { UseQueryResult } from '@tanstack/react-query'
import { InstallationStatementModel } from 'src/Libs/installation-statement/models/statement'
import { CACHE_TIME } from '../constants'
import { useInstallationStatementQueryFunctions } from './use-installation-statement-query-functions'
import { usePortalQuery } from 'src/Libs/api'

interface UseInstallationStatements {
  installationStatements: InstallationStatementModel[] | undefined
  installationStatementsLoading: boolean
  storageInstallationStatements: InstallationStatementModel | undefined
  storageInstallationStatementsLoading: boolean
  currentStorageInstallationStatement: InstallationStatementModel | undefined
  currentStorageInstallationStatementLoading: boolean
  refetchCurrentStorageInstallationStatement: () => Promise<UseQueryResult>
}

/**
 * Hook to get installation statemants
 * Note: currently BE only provides one Statement (the most recent one) per device link, but this will change in the future
 */
const useInstallationStatements = (installationId?: number, storageDeviceLinkId?: number, fetchCurrentOnly = false): UseInstallationStatements => {
  const {
    installationStatementsQueryFunction,
    installationStatementByDeviceLinkQueryFunction,
    currentInstallationStatementByDeviceLinkQueryFunction,
  } = useInstallationStatementQueryFunctions(installationId, storageDeviceLinkId)

  const { data: installationStatements, isLoading: installationStatementLoading } = usePortalQuery({
    queryKey: ['installation-statements', installationId],
    queryFn: installationStatementsQueryFunction,
    enabled: !!installationId && !!storageDeviceLinkId && !fetchCurrentOnly,
    staleTime: CACHE_TIME,
  })

  const { data: storageInstallationStatements, isLoading: storageInstallationStatementsLoading } = usePortalQuery({
    queryKey: ['storage-installation-statements', installationId, storageDeviceLinkId],
    queryFn: installationStatementByDeviceLinkQueryFunction,
    enabled: !!installationId && !!storageDeviceLinkId && !fetchCurrentOnly,
    staleTime: CACHE_TIME,
  })

  const {
    data: currentStorageInstallationStatement,
    isLoading: currentStorageInstallationStatementLoading,
    refetch: refetchCurrentStorageInstallationStatement,
  } = usePortalQuery({
    queryKey: ['current-storage-installation-statements', installationId, storageDeviceLinkId],
    queryFn: currentInstallationStatementByDeviceLinkQueryFunction,
    enabled: !!installationId && !!storageDeviceLinkId,
    staleTime: CACHE_TIME,
  })

  return {
    installationStatements: installationStatements || undefined,
    installationStatementsLoading: installationStatementLoading,
    storageInstallationStatements: storageInstallationStatements || undefined,
    storageInstallationStatementsLoading: storageInstallationStatementsLoading,
    currentStorageInstallationStatement: currentStorageInstallationStatement || undefined,
    currentStorageInstallationStatementLoading: currentStorageInstallationStatementLoading,
    refetchCurrentStorageInstallationStatement,
  }
}

export default useInstallationStatements
